import React, { useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import '@/index.css'
import Routes from '@/config/router/Routes'
import theme from '@/config/theme/pontio/theme'
import { Provider } from 'react-redux'
import { BrowserRouter as Router } from 'react-router-dom'
import { I18nextProvider } from 'react-i18next'
import store, { persistor } from '@/Store'
import i18nProvider from '@/config/i18n/i18nProvider'
import { PersistGate } from 'redux-persist/es/integration/react'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import 'dayjs/locale/es'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import axios from 'axios'
import { setupAxios } from '@/api/axios/index'
import { SnackbarProvider } from 'notistack'
import Snack from '@/config/notifications/Snack'
import { ThemeProvider } from '@mui/material/styles'
import { LoadScript } from '@react-google-maps/api'
import { CssBaseline, GlobalStyles } from '@mui/material'
import useClasses from '@/components/hooks/UseClasses'
import config from './config/env'
import { pdfjs } from 'react-pdf'
import * as serviceWorkerRegistration from './serviceWorkerRegistration'
import { AuthProvider, useAuth } from './components/app-context/AuthContext'
import { EmbeddedMessagingProvider } from './components/app-context/EmbeddedMessagingContext'
import GlobalLoader from './components/loading/GlobalLoader'

const AxiosSetup = () => {
  const auth = useAuth()
  setupAxios(axios, auth, store)
  return null
}

const {
  GOOGLE_MAPS_API_KEY,
  CRISP_CHAT,
  CRISP_CHAT_KEY,
  SF_ENABLED,
  SF_ORG_ID,
  SF_DEPLOYMENT_NAME,
  SF_BASE_URL,
  SF_SCRT2_URL,
  SF_BOOTSTRAP_URL,
} = config

const styles = () => ({
  contentRoot: {
    backgroundColor: theme.palette.alert.success.color + ' !important',
  },
  variantSuccess: {
    backgroundColor: theme.palette.alert.success.color + ' !important',
  },
  variantError: {
    backgroundColor: theme.palette.alert.error.color + ' !important',
  },
  variantInfo: {
    maxWidth: 500,
  },
  variantWarning: {
    backgroundColor: theme.palette.alert.warning.color + ' !important',
  },
})

// Me gustaria poder quitar esto, pero al quitarlo, desde sm a xl se ve como una mierda en pantallas mas pequeñas, asi que hace su labor...
// En XS necesitamos que siga siendo de 16px porque las pantallas de movil están expresamente diseñadas con estos tamaños en mente, habría que re-hacerlo todo si no.
// Idealmente me gustaria tener todo en 16px y que para tamaños distintos de pantalla, se adaptaran los diseños, entonces esto se podría borrar
const inputGlobalStyles = (
  <GlobalStyles
    styles={(theme) => ({
      html: {
        fontSize: '16px',
        [theme.breakpoints.down('xl')]: {
          fontSize: '12px',
        },
        [theme.breakpoints.down('sm')]: {
          fontSize: '16px',
        },
      },
    })}
  />
)
const libs = ['places', 'marker', 'geocoding']

const componentDidMount = (setStandard) => {
  setStandard(!window.location.origin.includes('fondoenergia'))
  if (CRISP_CHAT !== undefined && CRISP_CHAT === 'true') {
    if (window.$crisp === undefined) {
      window.$crisp = []
      window.CRISP_WEBSITE_ID = CRISP_CHAT_KEY
      ;(function () {
        var d = document
        var s = d.createElement('script')

        s.src = 'https://client.crisp.chat/l.js'
        s.async = 1
        d.getElementsByTagName('head')[0].appendChild(s)
      })()
    }
  }
}

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString()

const App = () => {
  const classes = useClasses(styles)
  // Igual deberiamos meter esto de otra forma más global para no estar pasándoselo a todos?
  // Mi idea es que siempre haya cabecera, y las pantallas de movil siempre sacarán cabecera con nuestro logo (el de fondo energia cuando toque, en base a esto)
  const [standard, setStandard] = useState(true)

  useEffect(() => {
    componentDidMount(setStandard)
  }, [])

  return (
    <I18nextProvider i18n={i18nProvider}>
      <PersistGate persistor={persistor}>
        <Provider store={store}>
          <AuthProvider store={store}>
            <AxiosSetup />
            <ThemeProvider theme={theme}>
              {inputGlobalStyles}
              <SnackbarProvider classes={classes} maxSnack={3}>
                <LocalizationProvider
                  adapterLocale={'es'}
                  dateAdapter={AdapterDayjs}
                  localeText={{
                    fieldYearPlaceholder: (params) => 'a'.repeat(params.digitAmount),
                    fieldMonthPlaceholder: (params) => (params.contentType === 'letter' ? 'mmmm' : 'mm'),
                    fieldDayPlaceholder: () => 'dd',
                  }}>
                  <CssBaseline />
                  <LoadScript
                    id="google-map-script"
                    googleMapsApiKey={GOOGLE_MAPS_API_KEY}
                    version="weekly"
                    libraries={libs}
                    language="es"
                    region="ES"
                    loadingElement={<div style={{ display: 'none' }}></div>}
                  />
                  <EmbeddedMessagingProvider
                    enabled={SF_ENABLED}
                    defaultVisible={true}
                    config={{
                      orgId: SF_ORG_ID,
                      deploymentName: SF_DEPLOYMENT_NAME,
                      baseUrl: SF_BASE_URL,
                      scrt2Url: SF_SCRT2_URL,
                      scriptUrl: SF_BOOTSTRAP_URL,
                      language: 'es',
                    }}>
                    <Router
                      future={{
                        v7_startTransition: false,
                        v7_relativeSplatPath: true,
                      }}>
                      <GlobalLoader />
                      <Routes standard={standard} />
                    </Router>
                  </EmbeddedMessagingProvider>
                  <Snack />
                </LocalizationProvider>
              </SnackbarProvider>
            </ThemeProvider>
          </AuthProvider>
        </Provider>
      </PersistGate>
    </I18nextProvider>
  )
}

window.addEventListener('keydown', (event) => {
  if (event.key === 'Enter') {
    let input = document.getElementById('searchTableFooter')
    if (input) {
      input.click()
    }
  }
})

serviceWorkerRegistration.register()

const container = document.getElementById('app')
const root = createRoot(container)
root.render(<App />)
